/**
 * GRE General Test percentile ranks.
 *
 * Source: ETS, "GRE General Test Interpretive Data" (Tables 1A, 1B and 1C), copyright 2026,
 * https://www.ets.org/content/dam/ets-org/pdfs/gre/gre-guide-table-1a.pdf
 * (linked from https://www.ets.org/gre/scoreresources). Retrieved 2026-10-16.
 *
 * Reference group: all individuals who tested between July 1, 2022 and June 30, 2025
 * (788,021 Verbal Reasoning, 790,864 Quantitative Reasoning and 785,720 Analytical
 * Writing test takers). A percentile rank here is "the percent of test takers scoring
 * lower than" the score. A blank cell in the ETS table "implies that percentile
 * information was not reported because there were no test takers above or below specified
 * scale score range"; blanks are stored as null.
 *
 * Table 1A means (standard deviations): Verbal 151.39 (8.43), Quantitative 157.62 (9.90),
 * Analytical Writing 3.46 (0.85).
 */

export const GRE_REFERENCE_START = "July 1, 2022";
export const GRE_REFERENCE_END = "June 30, 2025";

export type GreMeasure = "verbal" | "quant" | "writing";

/** [score, percent below] for scores 170 down to 130; null where ETS leaves the cell blank. */
const VERBAL_QUANT: readonly (readonly [
  number,
  number | null,
  number | null,
])[] = [
  [170, 99, 89],
  [169, 99, 85],
  [168, 98, 80],
  [167, 97, 75],
  [166, 96, 72],
  [165, 95, 67],
  [164, 93, 63],
  [163, 90, 60],
  [162, 88, 57],
  [161, 85, 53],
  [160, 82, 50],
  [159, 79, 47],
  [158, 76, 45],
  [157, 72, 42],
  [156, 68, 39],
  [155, 64, 37],
  [154, 59, 34],
  [153, 54, 31],
  [152, 48, 29],
  [151, 43, 26],
  [150, 39, 23],
  [149, 34, 21],
  [148, 30, 19],
  [147, 27, 16],
  [146, 24, 14],
  [145, 21, 12],
  [144, 18, 10],
  [143, 16, 9],
  [142, 14, 7],
  [141, 11, 6],
  [140, 10, 5],
  [139, 8, 4],
  [138, 6, 3],
  [137, 5, 2],
  [136, 4, 2],
  [135, 3, 1],
  [134, 2, 1],
  [133, 2, 1],
  [132, 1, null],
  [131, 1, null],
  [130, null, null],
];

const WRITING: readonly (readonly [number, number | null])[] = [
  [6, 99],
  [5.5, 98],
  [5, 93],
  [4.5, 85],
  [4, 63],
  [3.5, 40],
  [3, 16],
  [2.5, 7],
  [2, 3],
  [1.5, 1],
  [1, 1],
  [0.5, null],
  [0, null],
];

export interface GreRow {
  score: number;
  verbal: number | null;
  quant: number | null;
}

export const GRE_ROWS: readonly GreRow[] = VERBAL_QUANT.map(
  ([score, verbal, quant]) => ({ score, verbal, quant })
);

export const GRE_WRITING_ROWS: readonly {
  score: number;
  percentile: number | null;
}[] = WRITING.map(([score, percentile]) => ({ score, percentile }));

/** Whether the score is a possible score on the measure's scale. */
export function isGreScore(
  measure: GreMeasure,
  value: number | null
): value is number {
  if (value === null || !Number.isFinite(value)) {
    return false;
  }
  if (measure === "writing") {
    return value >= 0 && value <= 6 && Number.isInteger(value * 2);
  }
  return Number.isInteger(value) && value >= 130 && value <= 170;
}

export interface GrePercentile {
  /** Percent of test takers who scored lower, or null where ETS reports none. */
  below: number | null;
}

/** ETS's percentile rank for a valid score. */
export function grePercentile(
  measure: GreMeasure,
  score: number
): GrePercentile | null {
  if (!isGreScore(measure, score)) {
    return null;
  }
  if (measure === "writing") {
    const row = GRE_WRITING_ROWS.find((entry) => entry.score === score);
    return row ? { below: row.percentile } : null;
  }
  const row = GRE_ROWS.find((entry) => entry.score === score);
  return row ? { below: measure === "verbal" ? row.verbal : row.quant } : null;
}

/** The lowest score whose percentile rank is at least `target`, or null if none reaches it. */
export function lowestScoreForPercentile(
  measure: GreMeasure,
  target: number
): number | null {
  const rows =
    measure === "writing"
      ? GRE_WRITING_ROWS.map((row) => ({ score: row.score, p: row.percentile }))
      : GRE_ROWS.map((row) => ({
          score: row.score,
          p: measure === "verbal" ? row.verbal : row.quant,
        }));
  let best: number | null = null;
  for (const row of rows) {
    if (
      row.p !== null &&
      row.p >= target &&
      (best === null || row.score < best)
    ) {
      best = row.score;
    }
  }
  return best;
}

/** The Verbal plus Quantitative total, 260-340. ETS publishes no percentile for it. */
export function greTotal(verbal: number, quant: number): number {
  return verbal + quant;
}
